import type { DriveStep } from 'driver.js';

export const VENDOR_PORTAL_TOUR_SELECTORS = {
  sidebar: '[data-tour="vp-sidebar"]',
  versionTree: '[data-tour="vp-version-tree"]',
  timberLctf: '[data-tour="vp-timber-lctf"]',
  timberSupplier: '[data-tour="vp-timber-supplier"]',
  cncLctf: '[data-tour="vp-cnc-lctf"]',
  cncSupplier: '[data-tour="vp-cnc-supplier"]',
  help: '[data-tour="vp-help-btn"]',
} as const;

export type VendorPortalTourStepOptions = {
  showTimber: boolean;
  showCnc: boolean;
};

export function buildVendorPortalTourSteps(options: VendorPortalTourStepOptions): DriveStep[] {
  const steps: DriveStep[] = [
    {
      popover: {
        title: 'Welcome to the Supplier Portal',
        description:
          'LC Timber Frames publishes RFQ packages here. Review materials, download files, and submit quotes — or mark packages as not bidding.',
      },
    },
    {
      element: VENDOR_PORTAL_TOUR_SELECTORS.sidebar,
      popover: {
        title: 'Your projects',
        description:
          'Each row is a project. You will see the project number, descriptor, work type (Timber or CNC), your latest materials bid total, and whether a quote is still needed.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: VENDOR_PORTAL_TOUR_SELECTORS.versionTree,
      popover: {
        title: 'Package versions',
        description:
          'When we publish a revision, a new version appears here. Quote each version separately — your earlier responses stay on record.',
        side: 'right',
        align: 'start',
      },
    },
  ];

  if (options.showTimber) {
    steps.push(
      {
        element: VENDOR_PORTAL_TOUR_SELECTORS.timberLctf,
        popover: {
          title: 'Timber — from LCTF',
          description:
            'Download the timber list as CSV or PDF, then use Not bidding if you cannot quote this version.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: VENDOR_PORTAL_TOUR_SELECTORS.timberSupplier,
        popover: {
          title: 'Timber — your quote',
          description:
            'Upload your quote PDF with a total dollar amount. Prior uploads appear below this button for reference.',
          side: 'top',
          align: 'start',
        },
      },
    );
  }

  if (options.showCnc) {
    steps.push(
      {
        element: VENDOR_PORTAL_TOUR_SELECTORS.cncLctf,
        popover: {
          title: 'CNC — from LCTF',
          description:
            'Download the 3D model for this package version, then use Not bidding if you cannot quote.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: VENDOR_PORTAL_TOUR_SELECTORS.cncSupplier,
        popover: {
          title: 'CNC — your quote',
          description:
            'Upload your CNC quote PDF with a total dollar amount. Prior uploads appear below for reference.',
          side: 'top',
          align: 'start',
        },
      },
    );
  }

  steps.push({
    element: VENDOR_PORTAL_TOUR_SELECTORS.help,
    popover: {
      title: 'Need more detail?',
      description:
        'Open Help anytime for the full written guide. You can also replay this interactive tour from Help → Take a tour.',
      side: 'bottom',
      align: 'end',
    },
  });

  return steps;
}

export function vendorPortalTourStepCount(options: VendorPortalTourStepOptions): number {
  return buildVendorPortalTourSteps(options).length;
}
